import config from '../../config';
import { getToken } from '../lib/localStorageHandler';

export interface MultiFileAskAIRequest {
  fileIds: string[];
  question: string;
  sessionId?: string | null;
}

export interface SummarizedMultiFileAskAIRequest {
  fileIds: string[];
  question: string;
  sessionId?: string;
  modelName?: string;
}

export interface MultiFileAskAIResponse {
  success: boolean;
  data?: {
    sessionId: string;
    answer: {
      role: string;
      content: string;
      partial: boolean;
      refusal: string;
    };
    fileCount: number;
  };
  message?: string;
}

export interface DeleteSessionResponse {
  success: boolean;
  message?: string;
}

export interface SessionListItem {
  _id: string;
  fileIds: string[];
  conversation: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
  }>;
  createdAt: string;
  updatedAt: string;
  teamId?: string;
}

export interface GetSessionsResponse {
  success: boolean;
  data?: {
    sessions: SessionListItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  message?: string;
}

/**
 * Ask a question across multiple files using AI
 * @param teamId - The team ID
 * @param request - Request payload containing fileIds, question, and optional sessionId
 * @returns Response with AI answer and session information
 */
export const askMultiFileQuestion = async (
  teamId: string,
  request: MultiFileAskAIRequest,
): Promise<MultiFileAskAIResponse> => {
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file/${teamId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze recordings');
  }

  return response.json();
};

/**
 * Get multi-file Ask AI sessions history
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param teamId - Optional team ID filter
 * @returns Response with sessions list and pagination
 */
export const getMultiFileSessions = async (
  page: number = 1,
  limit: number = 20,
  teamId?: string,
): Promise<GetSessionsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (teamId) {
    params.append('teamId', teamId);
  }

  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file/sessions?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch sessions');
  }

  return response.json();
};

/**
 * Delete a multi-file Ask AI session
 * @param sessionId - The session ID to delete
 * @returns Response indicating success or failure
 */
export const deleteMultiFileSession = async (
  sessionId: string,
): Promise<DeleteSessionResponse> => {
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file/session/${sessionId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete session');
  }

  return response.json();
};

/**
 * Ask a question about all recordings in a folder
 * @param teamId - The team ID
 * @param folderId - The folder ID
 * @param question - The question to ask
 * @param sessionId - Optional session ID for conversation continuity
 * @returns Response with AI answer and session information
 */
export const askFolderQuestion = async (
  teamId: string,
  folderId: string,
  question: string,
  sessionId?: string | null,
): Promise<MultiFileAskAIResponse> => {
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/folder/${teamId}/${folderId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        question,
        sessionId,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze folder recordings');
  }

  return response.json();
};

/**
 * Ask a question across multiple files using AI with summarization
 * This endpoint uses a more efficient summarization approach
 * @param teamId - The team ID
 * @param request - Request payload containing fileIds, question, and optional model settings
 * @returns Response with AI answer
 */
export const askMultiFileSummarizedQuestion = async (
  teamId: string,
  request: SummarizedMultiFileAskAIRequest,
): Promise<MultiFileAskAIResponse> => {
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file-summarized/${teamId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze recordings');
  }

  return response.json();
};

/**
 * Ask a question across multiple files using AI with real-time streaming
 * @param teamId - The team ID
 * @param request - Request payload containing fileIds, question, and optional model settings
 * @param onChunk - Callback function called for each text chunk received
 * @param options - Optional configuration for streaming behavior
 * @returns Promise with sessionId after stream completes
 */
export const askMultiFileQuestionStream = async (
  teamId: string,
  request: SummarizedMultiFileAskAIRequest,
  onChunk: (chunk: string) => void,
  options?: { streamDelay?: number; signal?: AbortSignal },
): Promise<{ sessionId: string }> => {
  const streamDelay = options?.streamDelay ?? 15; // Default 15ms delay for smooth, fast streaming
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file-stream/${teamId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(request),
      signal: options?.signal,
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze recordings');
  }

  // Get session ID from response headers
  const sessionId = response.headers.get('X-Session-Id') || '';

  // Parse SSE stream
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let displayedText = '';

  // Word-by-word display queue
  const wordQueue: string[] = [];

  // Function to display words from queue one by one
  const displayWords = async () => {
    while (wordQueue.length > 0) {
      const word = wordQueue.shift();
      if (word !== undefined) {
        displayedText += word;
        onChunk(displayedText);

        if (streamDelay > 0 && wordQueue.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, streamDelay));
        }
      }
    }
  };

  try {
    let done = false;
    while (!done) {
      // eslint-disable-next-line no-await-in-loop
      const result = await reader.read();
      done = result.done;

      if (done) {
        // Display any remaining words
        // eslint-disable-next-line no-await-in-loop
        await displayWords();
        console.log('[Streaming] Stream complete');
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(result.value, { stream: true });

      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      // Parse chunks and split into words
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.chunk) {
              // Split chunk into words (including spaces and punctuation)
              // This regex splits on word boundaries but keeps spaces
              const words = data.chunk.split(/(\s+)/);

              // Add words to queue
              words.forEach((word: string) => {
                if (word) {
                  wordQueue.push(word);
                }
              });

              // Display queued words
              // eslint-disable-next-line no-await-in-loop
              await displayWords();
            }
          } catch (e) {
            console.error(
              '[Streaming] Failed to parse SSE chunk:',
              e,
              'Line:',
              line,
            );
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  return { sessionId };
};
